/**
 * InstallAppPrompt — "Add to Home Screen", replacing the App Store banner.
 *
 * Replaced `DownloadAppBanner` on 2026-08-21. That component sent mobile
 * visitors to the App Store listing, and the live listing describes a
 * different product from the one the First 100 outreach describes — it can't
 * be corrected without a new binary we currently can't ship. So the outreach
 * points at the web app, and the one thing the site must not do is bounce
 * people straight into that listing. The `apple-itunes-app` meta tag in
 * index.html was removed for the same reason; both go back together, or not
 * at all.
 *
 * Deliberate differences from the banner it replaces:
 *   - **Mobile only.** A desktop visitor has nothing to install and got a
 *     two-store pill that was pure noise.
 *   - **Silent when already installed.** `display-mode: standalone` (and
 *     iOS's `navigator.standalone`) mean they're reading this INSIDE the
 *     installed app; prompting there is how you look broken.
 *   - **The banner explains nothing, the modal does.** "Add to Home Screen"
 *     is three taps most people have never made, and on iOS it cannot be
 *     triggered by script — only described.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "@/hooks/useAuth";
import { X, Share, Plus, MoreVertical, Download, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Public routes where the prompt may appear.
 *
 * `/` is deliberately in here and covers two different screens: it is the
 * Gateway for a logged-out visitor and MainApp for a signed-in one. Both are
 * moments where installing makes sense — the second more than the first,
 * since someone who has just signed up is the person most likely to want the
 * icon on their home screen.
 *
 * `/login` and `/reset-password` stay OUT: at phone width a bottom banner sat
 * on top of the signup form's submit button (found in browser testing,
 * 2026-08-04). Someone mid-signup is already committed and the prompt can
 * only get in the way of the most important button on the page.
 *
 * `/partners` and `/creators` stay OUT too: those are read by a group leader
 * doing due diligence, not by someone about to use the app daily.
 */
const PROMPT_VISIBLE_PATHS = new Set([
  "/",
  "/landing",
  "/about",
  "/faq",
  "/support",
  "/why",
]);

/** How long after first paint before the install banner may appear. Long
 *  enough that it never competes with the page arriving — see T23. */
const FIRST_PAINT_GRACE_MS = 45_000;

const DISMISS_KEY = "trybeup_install_prompt_dismissed_at";
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

type Platform = "ios-web" | "android-web" | "desktop" | "in-app";

/** The Chrome/Edge install event. Not in lib.dom yet, hence the local shape. */
type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function detectPlatform(): Platform {
  if (Capacitor.isNativePlatform()) return "in-app";
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports as Macintosh, so the touch check catches it.
  const isIOS =
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document);
  if (isIOS) return "ios-web";
  if (/Android/i.test(ua)) return "android-web";
  return "desktop";
}

/** True when the page is already running as an installed app. */
function isAlreadyInstalled(): boolean {
  try {
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
    // iOS Safari predates display-mode and uses this instead.
    if ((window.navigator as unknown as { standalone?: boolean }).standalone) return true;
  } catch {
    /* matchMedia unavailable — assume not installed, worst case we prompt once */
  }
  return false;
}

function isRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export default function InstallAppPrompt() {
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [installEvent, setInstallEvent] = useState<InstallPromptEvent | null>(null);
  const location = useLocation();
  const { user } = useAuth();
  const onAllowedPath = PROMPT_VISIBLE_PATHS.has(location.pathname);

  // ── One banner at a time, and never this one on the demo (T23) ──────
  // A visitor evaluating somebody else's account should not be asked to
  // install the app on their phone — they haven't decided they want it yet,
  // and the account isn't theirs. It also competed for the first paint with
  // the demo strip, which is the banner that has to win: it's the one
  // telling you which account you're in.
  const isDemo = Boolean(user?.isDemo);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    if (p === "in-app" || p === "desktop") return;
    if (isAlreadyInstalled()) return;
    if (isRecentlyDismissed()) return;
    // Deliberately long. This used to be 1200ms, which put the banner on
    // screen during first paint, on top of whatever else was arriving —
    // three banners could be competing at once (T23). Installing is worth
    // asking about once someone has actually used the app for a minute,
    // not while they're still working out what it is.
    const t = setTimeout(() => setVisible(true), FIRST_PAINT_GRACE_MS);
    return () => clearTimeout(t);
  }, []);

  // Chrome on Android fires this when the app is installable. Capturing it
  // turns the modal's button into a real one-tap install instead of a list of
  // instructions. iOS Safari never fires it — there, instructions are all
  // that exist.
  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault(); // stop Chrome's own mini-infobar; we have our own UI
      setInstallEvent(e as InstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    const onInstalled = () => {
      setVisible(false);
      setModalOpen(false);
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* localStorage unavailable — it just won't re-suppress next visit */
    }
  };

  const handleAndroidInstall = async () => {
    if (!installEvent) return;
    try {
      await installEvent.prompt();
      await installEvent.userChoice;
      setInstallEvent(null);
      setModalOpen(false);
      dismiss();
    } catch {
      /* user cancelled — leave the modal open so the manual steps are there */
    }
  };

  const showBanner =
    visible && onAllowedPath && !isDemo && (platform === "ios-web" || platform === "android-web");

  return (
    <>
      {showBanner && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 border-t border-primary/40 bg-background/95 backdrop-blur px-4 py-3 shadow-2xl"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
          role="dialog"
          aria-label="Install TrybeUP on your phone"
        >
          <div className="flex items-center gap-3 max-w-md mx-auto">
            <img
              src="/icon-192.png"
              alt=""
              className="flex-shrink-0 w-11 h-11 rounded-xl shadow-md"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground leading-tight">
                Put TrybeUP on your home screen
              </p>
              <p className="text-xs text-muted-foreground leading-tight mt-0.5">
                Opens like an app. Takes about ten seconds.
              </p>
            </div>
            <button
              onClick={() => setModalOpen(true)}
              className="flex-shrink-0 rounded-lg bg-foreground text-background text-sm font-semibold px-3 py-2 hover:bg-foreground/90 active:scale-[0.98] transition"
              data-testid="install-app-banner-cta"
            >
              Show me
            </button>
            <button
              onClick={dismiss}
              aria-label="Not now"
              className="flex-shrink-0 p-1.5 rounded-lg text-muted-foreground hover:bg-muted transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-left">Add TrybeUP to your home screen</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              It gets its own icon and opens full-screen, without the browser bar. Everything
              stays exactly where it is — same account, same data.
            </p>

            {platform === "ios-web" && (
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center mt-0.5">1</span>
                  <span className="text-sm text-foreground leading-relaxed">
                    Tap the <strong>Share</strong> button <Share className="w-4 h-4 inline mx-0.5 -mt-0.5" /> at the
                    bottom of Safari.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center mt-0.5">2</span>
                  <span className="text-sm text-foreground leading-relaxed">
                    Scroll down and tap <strong>Add to Home Screen</strong>{" "}
                    <Plus className="w-4 h-4 inline mx-0.5 -mt-0.5" />.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center mt-0.5">3</span>
                  <span className="text-sm text-foreground leading-relaxed">
                    Tap <strong>Add</strong>. The TrybeUP icon appears on your home screen.
                  </span>
                </li>
              </ol>
            )}

            {platform === "android-web" && (
              <>
                {installEvent ? (
                  <button
                    onClick={handleAndroidInstall}
                    className="w-full rounded-xl bg-primary text-primary-foreground font-semibold py-3 flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.99] transition"
                  >
                    <Download className="w-4 h-4" />
                    Install TrybeUP
                  </button>
                ) : (
                  <ol className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center mt-0.5">1</span>
                      <span className="text-sm text-foreground leading-relaxed">
                        Tap the <strong>menu</strong> <MoreVertical className="w-4 h-4 inline mx-0.5 -mt-0.5" /> at
                        the top right of Chrome.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center mt-0.5">2</span>
                      <span className="text-sm text-foreground leading-relaxed">
                        Tap <strong>Add to Home screen</strong> (or <strong>Install app</strong>).
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center mt-0.5">3</span>
                      <span className="text-sm text-foreground leading-relaxed">
                        Confirm, and the icon appears on your home screen.
                      </span>
                    </li>
                  </ol>
                )}
              </>
            )}

            <div className="rounded-xl bg-muted/50 border border-border p-3 space-y-1.5">
              <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-green-500" />
                Reminders still reach you either way
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Your daily reminder arrives by email whether you install this or not — so
                nothing breaks if you'd rather just use the browser.
              </p>
            </div>

            <button
              onClick={() => { setModalOpen(false); dismiss(); }}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition py-1"
            >
              Not now
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
